/** @file shiftTrack: a node track's frame-key entries moved with a frame range (length unchanged). */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { ClipTrack } from '@zilo/pose3d-format/types/clip';
import { throwError } from '@zilo/pose3d-std/errors/throwError';

/** `old` with entries lo..hi moved by `s`; the vacated entries take the moved range's edge value. */
function moveEntries(old: readonly number[], lo: number, hi: number, s: number): number[] {
  const out = [...old];
  const inside = (i: number): boolean => i >= 0 && i < old.length;
  const [vacFrom, vacTo, edge] = s > 0 ? [lo, lo + s - 1, old[lo] ?? 0] : [hi + s + 1, hi, old[hi] ?? 0];
  for (let i = vacFrom; i <= vacTo; i++) if (inside(i)) out[i] = edge;
  for (let i = lo; i <= hi; i++) if (inside(i + s)) out[i + s] = old[i] ?? 0;
  return out;
}

/**
 * Entry i covers frames [i·hold, (i+1)·hold); the entries of [from, to] move by `by / hold` (`VALIDATION` unless `by`
 * is a multiple of `hold`); vacated entries take the moved range's edge value; entries past the end drop off.
 */
export function shiftTrack(track: ClipTrack, from: number, to: number, by: number, path = '/input/by'): ClipTrack {
  const old = track.frameKey;
  if (old === undefined || old.length === 0) return structuredClone(track);
  const hold = track.hold ?? 1;
  if (by % hold !== 0) throwError(ERROR_CODES.VALIDATION, { by, hold }, { path, hint: `shift by a multiple of the track hold ${hold}` });
  const lo = Math.floor(from / hold);
  const hi = Math.min(old.length - 1, Math.floor(to / hold));
  const frameKey = lo > hi || by === 0 ? [...old] : moveEntries(old, lo, hi, by / hold);
  return { ...structuredClone(track), frameKey };
}
